import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';

const Login = () => {
  const { login, isAuthenticated, loading, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState(null);
  const [bypassLoading, setBypassLoading] = useState(false);

  // Determine if we're on production (hide bypass button on production)
  const isProduction = window.location.hostname === 'contract-admin.absinc.app';
  
  // Use relative URLs - the frontend and backend are on the same domain
  const backendUrl = '';

  useEffect(() => {
    const error = searchParams.get('error');
    const message = searchParams.get('message');
    
    if (error) {
      switch (error) {
        case 'domain_not_allowed':
          setErrorMessage(message || 'Only absinc.com email addresses are allowed.');
          break;
        case 'no_email':
          setErrorMessage('Email address not provided by identity provider.');
          break;
        case 'token_exchange_failed':
          setErrorMessage('Failed to complete authentication. Please try again.');
          break;
        case 'invalid_state':
          setErrorMessage('Invalid authentication state. Please try again.');
          break;
        default:
          setErrorMessage(message || 'An authentication error occurred. Please try again.');
      }
    }
  }, [searchParams]);

  // Navigate to home when authenticated
  useEffect(() => {
    if (isAuthenticated && !loading) {
      console.log('User authenticated, navigating to home');
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const handleBypassLogin = async () => {
    setBypassLoading(true);
    setErrorMessage(null);
    
    try {
      const response = await fetch(`${backendUrl}/api/auth/bypass`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        setTimeout(async () => {
          await checkAuth();
          setTimeout(() => {
            setBypassLoading(false);
          }, 1000);
        }, 100);
      } else {
        const data = await response.json().catch(() => ({}));
        setErrorMessage(data.detail || `Bypass login failed. Please try again.`);
        setBypassLoading(false);
      }
    } catch (err) {
      setErrorMessage(`Failed to connect to server: ${err.message}`);
      setBypassLoading(false);
    }
  };

  if (loading && !bypassLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1758761421572-f503d45c03d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjBibHVlcHJpbnRzJTIwYXJjaGl0ZWN0dXJlJTIwbW9kZXJuJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzczMjEwMDMyfDA&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Dark overlay for better readability */}
      <div className="absolute inset-0 bg-black/50"></div>
      
      <div className="max-w-md w-full mx-4 relative z-10">
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <img
              src={`${process.env.PUBLIC_URL || ''}/abs-logo.png`}
              alt="ABS Logo"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-gray-900">Contract Admin</h1>
            <p className="text-gray-500 mt-2">Sign in to continue</p>
          </div>

          {errorMessage && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-700">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">{errorMessage}</span>
              </div>
            </div>
          )}

          {/* SSO Login Button */}
          <button
            onClick={login}
            disabled={bypassLoading}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Sign in with JumpCloud
          </button>

          {/* Bypass SSO Button - Only shown on preview/dev */}
          {!isProduction && (
            <>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">or</span>
                </div>
              </div>

              <button
                onClick={handleBypassLogin}
                disabled={bypassLoading}
                className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-amber-50 text-amber-700 font-medium rounded-lg hover:bg-amber-100 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 border border-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bypassLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-600"></div>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                )}
                {bypassLoading ? 'Signing in...' : 'Bypass SSO (Preview Only)'}
              </button>
            </>
          )}

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400">
              Access restricted to <span className="font-medium">@absinc.com</span> email addresses
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full">
            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-xs text-green-700 font-medium">Secured with OpenID Connect</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
